import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { DELETION_CONFIRMATION_WORD, DELETION_WINDOW_DAYS, type DeletionImpact, type DeletionRequestView } from "@townsy/contracts";
import { errorMessage, isApiError } from "../../api/client";
import {
  cancelDeletion,
  downloadMyData,
  invalidatePrivacy,
  newIdempotencyKey,
  privacyKeys,
  requestDeletion,
  useDeletionImpactQuery,
  useDeletionStatusQuery,
} from "../../api/privacy";
import { stageLabel } from "../../homes/homeLogic";
import { formatCents, formatUtcDateTime } from "../../lib/format";
import { formatCoins } from "../../lib/wallet";

/**
 * Account page (ADR-0037; founder 2026-10-22: "Return holdings", "7 days"): "Download my data" and "Delete my account".
 * The server decides everything (what is lost, what delays it, when it happens); this only shows it and asks for the
 * confirmation. Nothing here moves money, ownership or items.
 */

const KEPT = [
  "Payment, order and receipt records and your coin and credit history, with your name removed. We must keep financial records.",
  "Safety reports and the messages they hold, for their review.",
  "Visit statistics, which never identified you.",
];

/** The banner while a deletion is pending: the date, why it waits (if it does), and Cancel. */
export function DeletionPendingBanner(props: {
  request: DeletionRequestView;
  busy: boolean;
  error: string | null;
  onCancel(): void;
  /** Compact (in the town): one line and a link to the account page. */
  compact?: boolean;
  now?: Date;
}) {
  const { request, busy, error, onCancel, compact } = props;
  const due = new Date(request.scheduledFor).getTime() <= (props.now ?? new Date()).getTime();
  const when = formatUtcDateTime(request.scheduledFor);
  return (
    <div className="notice notice-danger deletion-banner" role="status" data-testid="deletion-banner">
      <p style={{ margin: 0 }}>
        {due ? (
          <>
            <strong>Your account is due to be deleted</strong> (since {when}).
          </>
        ) : (
          <>
            <strong>Your account will be deleted on {when}.</strong> Until then everything works as usual.
          </>
        )}
        {compact ? (
          <>
            {" "}
            <Link to="/account">Details</Link>
          </>
        ) : null}
      </p>
      {!compact && request.waiting.length ? (
        <div className="small" style={{ marginTop: 6 }} data-testid="deletion-waiting">
          It is waiting for:
          <ul className="privacy-list">
            {request.waiting.map((w) => (
              <li key={w.code}>{w.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {request.canCancel ? (
        <div className="row" style={{ marginTop: 6 }}>
          <button type="button" className="btn btn-sm" disabled={busy} onClick={onCancel} data-testid="deletion-cancel">
            {busy ? "Canceling…" : "Cancel deletion"}
          </button>
        </div>
      ) : null}
      {error ? (
        <div className="small" role="alert" style={{ marginTop: 6 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}

/** Exactly what the account loses (each store and home by address, coins, credit, rentals, offers). */
export function DeletionLossList({ impact }: { impact: DeletionImpact }) {
  const items: string[] = [];
  for (const s of impact.stores) items.push(`Your store at ${s.address} goes back to the town for sale.`);
  for (const h of impact.homes) {
    const placed = h.placedItems ? `, ${h.placedItems} item${h.placedItems === 1 ? "" : "s"} placed` : "";
    items.push(`Your home at ${h.address} (${stageLabel(h.stage)}${placed}) goes back to the town for sale.`);
  }
  if (impact.inventoryItems) items.push(`Your ${impact.inventoryItems} home item${impact.inventoryItems === 1 ? "" : "s"} go${impact.inventoryItems === 1 ? "es" : ""} with it.`);
  if (impact.coins) items.push(`Your ${formatCoins(impact.coins)} are forfeited.`);
  if (impact.creditCents) items.push(`Your ${formatCents(impact.creditCents)} of Townsy Credit is forfeited. It is store credit and is never paid out.`);
  for (const r of impact.billboardRentals) items.push(`Your billboard rental on ${r.label} (until ${r.lastDay}, UTC) ends early, with no refund.`);
  for (const o of impact.runningOffers) items.push(`Your offers "${o.title}"${o.address ? ` at ${o.address}` : ""} stop.`);
  // Founder 2026-10-23 ("Forfeit"): unspent card-paid task budget is not refunded.
  for (const b of impact.campaignBudgets) {
    const inUse = b.reservedCents ? ` Tasks someone is doing right now may still use up to ${formatCents(b.reservedCents)} of it.` : "";
    items.push(`The unused budget of your sponsored tasks "${b.title}" (${formatCents(b.availableCents + b.reservedCents)}) is forfeited, with no refund.${inUse}`);
  }
  return (
    <ul className="privacy-list" data-testid="deletion-losses">
      {items.length ? items.map((t) => <li key={t}>{t}</li>) : <li>You own no stores, homes, coins or Townsy Credit right now.</li>}
      <li>Your name, email address and sign-in are removed, and so are the private messages you sent. You cannot sign in again.</li>
    </ul>
  );
}

export interface DeleteAccountFormValues {
  password: string;
  confirmation: string;
  acknowledged: boolean;
}

/** Why the confirm button is disabled (null when it can be pressed). */
export function deleteFormProblem(v: DeleteAccountFormValues, passwordRequired: boolean): string | null {
  if (v.confirmation.trim() !== DELETION_CONFIRMATION_WORD) return `Type ${DELETION_CONFIRMATION_WORD} to confirm.`;
  if (!v.acknowledged) return "Tick the box to say you understand what you lose.";
  if (passwordRequired && !v.password) return "Enter your password.";
  return null;
}

/** The impact, the 7-day explanation, what is kept, what would delay it, and the typed confirmation. */
export function DeleteAccountPanel(props: {
  impact: DeletionImpact;
  values: DeleteAccountFormValues;
  onChange(v: DeleteAccountFormValues): void;
  onSubmit(): void;
  onClose(): void;
  busy: boolean;
  error: string | null;
}) {
  const { impact, values, onChange, busy, error } = props;
  const problem = deleteFormProblem(values, impact.passwordRequired);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!problem && !busy) props.onSubmit();
  };
  return (
    <form className="auth-forms deletion-panel" onSubmit={submit} data-movement-block="true" data-testid="deletion-panel">
      <div>
        <div className="section-title">What you lose</div>
        <DeletionLossList impact={impact} />
      </div>
      <div>
        <div className="section-title">What happens</div>
        <ul className="privacy-list">
          <li>Your account is deleted {impact.windowDays} days after you confirm. Until then everything works as usual, and you can cancel here at any time.</li>
          <li>If a payment, refund or dispute is still open on that day, we finish it first and delete your account afterwards.</li>
          <li>We email you when you ask and again when it is done.</li>
        </ul>
      </div>
      <div>
        <div className="section-title">What Townsy keeps</div>
        <ul className="privacy-list">
          {KEPT.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </div>
      {impact.delays.length ? (
        <div className="notice notice-warning small" data-testid="deletion-delays">
          Right now, deleting would also wait for:
          <ul className="privacy-list">
            {impact.delays.map((d) => (
              <li key={d.code}>{d.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {impact.passwordRequired ? (
        <label className="field">
          Your password
          <input type="password" name="password" autoComplete="current-password" value={values.password} onChange={(e) => onChange({ ...values, password: e.target.value })} data-testid="deletion-password" />
        </label>
      ) : (
        <p className="hint">For your safety you need to have signed in within the last 10 minutes. If you signed in earlier, sign out and in again first.</p>
      )}
      <label className="field">
        Type {DELETION_CONFIRMATION_WORD} to confirm
        <input
          type="text"
          name="confirmation"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={values.confirmation}
          onChange={(e) => onChange({ ...values, confirmation: e.target.value })}
          data-testid="deletion-confirmation"
        />
      </label>
      <label className="privacy-check">
        <input type="checkbox" checked={values.acknowledged} onChange={(e) => onChange({ ...values, acknowledged: e.target.checked })} data-testid="deletion-ack" />
        <span>I understand that I lose everything listed above, and that coins and Townsy Credit are not paid out.</span>
      </label>
      {error ? (
        <div className="notice notice-danger small" role="alert">
          {error}
        </div>
      ) : null}
      <div className="row">
        <button type="submit" className="btn btn-danger" disabled={Boolean(problem) || busy} aria-describedby="deletion-submit-hint" data-testid="deletion-submit">
          {busy ? "Sending…" : `Delete my account in ${impact.windowDays} days`}
        </button>
        <button type="button" className="btn" onClick={props.onClose} disabled={busy}>
          Keep my account
        </button>
      </div>
      <span id="deletion-submit-hint" className="hint">
        {problem ?? `You can still cancel for ${impact.windowDays} days.`}
      </span>
    </form>
  );
}

function DownloadMyData() {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const run = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const name = await downloadMyData();
      setMessage({ tone: "success", text: `Downloaded ${name}.` });
    } catch (err) {
      setMessage({ tone: "error", text: errorMessage(err, "The download failed. Try again in a minute.") });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="section" data-testid="account-my-data">
      <div className="section-title">Your data</div>
      <p className="small" style={{ margin: "0 0 6px" }}>
        Download a copy of what Townsy stores about your account: your profile and avatar, coins and credit history, orders and payments, properties and home
        items, billboard rentals, offers, sponsored tasks, private messages, the reports you filed and your mutes. It is one JSON file. You can do this up to 10
        times a day.
      </p>
      <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void run()} data-testid="download-my-data">
        {busy ? "Preparing…" : "Download my data"}
      </button>
      {message ? (
        <div className={message.tone === "error" ? "small error-text" : "small"} role={message.tone === "error" ? "alert" : "status"} style={{ marginTop: 6 }}>
          {message.text}
        </div>
      ) : null}
    </div>
  );
}

const EMPTY_FORM: DeleteAccountFormValues = { password: "", confirmation: "", acknowledged: false };

/** Data export + account deletion on the account page. */
export function PrivacySection({ signedIn }: { signedIn: boolean }) {
  const qc = useQueryClient();
  const status = useDeletionStatusQuery(signedIn);
  const [open, setOpen] = useState(false);
  const impact = useDeletionImpactQuery(signedIn && open);
  const [values, setValues] = useState<DeleteAccountFormValues>(EMPTY_FORM);
  const [key, setKey] = useState(newIdempotencyKey);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!signedIn) return null;
  const pending = status.data?.request ?? null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await requestDeletion({ password: values.password || undefined, confirmation: values.confirmation.trim(), acknowledgeLoss: true }, key);
      qc.setQueryData(privacyKeys.status, res);
      setOpen(false);
      setValues(EMPTY_FORM);
      setKey(newIdempotencyKey());
      await invalidatePrivacy(qc);
    } catch (err) {
      // A wrong password never used the key; anything else final gets a fresh key for the next try.
      if (!(isApiError(err) && err.code === "PASSWORD_INCORRECT")) setKey(newIdempotencyKey());
      setError(errorMessage(err, "Your request was not sent. Try again."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <DownloadMyData />
      <div className="section" data-testid="account-delete">
        <div className="section-title">Delete my account</div>
        {pending ? (
          <p className="small" style={{ margin: 0 }} data-testid="deletion-scheduled">
            Your account is scheduled to be deleted on {formatUtcDateTime(pending.scheduledFor)}. You can cancel it with the button at the top of this page.
          </p>
        ) : open ? (
          impact.data ? (
            <DeleteAccountPanel
              impact={impact.data}
              values={values}
              onChange={setValues}
              onSubmit={() => void submit()}
              onClose={() => {
                setOpen(false);
                setValues(EMPTY_FORM);
                setError(null);
              }}
              busy={busy}
              error={error}
            />
          ) : impact.error ? (
            <p className="small error-text" role="alert">
              Couldn't load what your account holds: {errorMessage(impact.error)}{" "}
              <button type="button" className="btn btn-sm" onClick={() => void impact.refetch()}>
                Retry
              </button>
            </p>
          ) : (
            <p className="muted small">Loading what your account holds…</p>
          )
        ) : (
          <>
            <p className="small" style={{ margin: "0 0 6px" }}>
              Your stores and home go back to the town, your coins and Townsy Credit are forfeited, and your account is anonymised {DELETION_WINDOW_DAYS} days
              after you confirm. You see the full list first.
            </p>
            <button type="button" className="btn btn-sm btn-danger" aria-expanded={false} onClick={() => setOpen(true)} data-testid="deletion-open">
              Delete my account…
            </button>
          </>
        )}
      </div>
    </>
  );
}

/**
 * The banner while a deletion is pending, with the date and Cancel: at the top of the account page (with the reasons it
 * waits), and compact under the town's HUD (with a link to the account page).
 */
export function DeletionNotice({ signedIn, compact = false }: { signedIn: boolean; compact?: boolean }) {
  const qc = useQueryClient();
  const status = useDeletionStatusQuery(signedIn);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = status.data?.request ?? null;
  if (!signedIn || !request) return null;
  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await cancelDeletion();
    } catch (err) {
      setError(errorMessage(err, "The deletion could not be canceled. Try again on your Account page."));
    } finally {
      await invalidatePrivacy(qc);
      setBusy(false);
    }
  };
  return <DeletionPendingBanner request={request} busy={busy} error={error} onCancel={() => void cancel()} compact={compact} />;
}
